/**
 * Browser polyfills for Node.js compatibility
 * This file provides browser-compatible implementations of Node.js globals
 * 
 * IMPORTANT: This file must be imported before any other code that relies on Node.js APIs
 */

// Polyfill for global - used by many Node.js libraries
if (typeof window !== 'undefined') {
  // @ts-ignore - Make window the global object
  window.global = window;
}

// Buffer polyfill to prevent "Buffer is undefined" errors
// Using the most compatible approach with just the essentials
if (typeof window !== 'undefined') {
  // @ts-ignore - Full bypass of type checking for Buffer implementation
  window.Buffer = {
    // @ts-ignore - Force type override for isBuffer
    isBuffer: function isBuffer(obj) {
      return !!obj && 
             typeof obj === 'object' && 
             obj.constructor && 
             typeof obj.constructor.isBuffer === 'function' && 
             obj.constructor.isBuffer(obj);
    },
    // @ts-ignore - Force type override for from
    from: function from(data) {
      // Simplest implementation to avoid TypeScript issues
      if (data instanceof Uint8Array) return data;
      if (Array.isArray(data)) return new Uint8Array(data);
      if (typeof data === 'string') {
        const arr = new Uint8Array(data.length);
        for (let i = 0; i < data.length; i++) {
          arr[i] = data.charCodeAt(i);
        }
        return arr;
      }
      return new Uint8Array(0);
    }
  };
}

// Create and attach a minimal process object to the global window object
// This is needed for various libraries that expect Node.js environment
if (typeof window !== 'undefined') {
  // @ts-ignore - we're deliberately creating a simplified process object
  window.process = {
    env: {
      // Use Vite's environment variables
      NODE_ENV: import.meta.env.MODE || 'development',
      BROWSER: 'true'
    },
    // Add version info to satisfy version checks
    version: '16.0.0',
    versions: { 
      node: '16.0.0',
      // Add required ProcessVersions properties
      http_parser: '1.0.0',
      v8: '9.0.0',
      ares: '1.0.0',
      uv: '1.0.0',
      zlib: '1.0.0',
      modules: '88',
      openssl: '1.0.0'
    },
    // Browser platform identifier - using linux as it's the most compatible
    platform: 'linux' as any,
    // Stub for process.nextTick that uses setTimeout
    nextTick: (callback: Function) => setTimeout(callback, 0),
    // Stub console output streams with proper return values
    stdout: { 
      write: (msg: string) => {
        console.log(msg);
        return true;
      } 
    } as any,
    stderr: { 
      write: (msg: string) => {
        console.error(msg);
        return true;
      } 
    } as any,
    // Prevent errors with process.argv
    argv: [],
    // Add missing properties that some libraries might check
    cwd: () => '/',
    exit: ((code?: number) => { /* no-op */ }) as any
  };
}

// Define the global process interface to match our polyfill
declare global {
  interface Window {
    global: Window & typeof globalThis;
    Buffer: {
      isBuffer: (obj: any) => obj is Buffer;
      from: (data: any, encoding?: BufferEncoding) => Uint8Array;
    };
    process: {
      env: Record<string, string | boolean>;
      version: string;
      versions: { 
        node: string; 
        [key: string]: string;
        http_parser: string;
        v8: string;
        ares: string;
        uv: string;
        zlib: string;
        modules: string;
        openssl: string;
      };
      platform: string;
      nextTick: (callback: Function) => void;
      stdout: { 
        write: (
          msg: string | Uint8Array, 
          encoding?: BufferEncoding, 
          callback?: (err?: Error) => void
        ) => boolean 
      };
      stderr: { 
        write: (
          msg: string | Uint8Array, 
          encoding?: BufferEncoding, 
          callback?: (err?: Error) => void
        ) => boolean 
      };
      argv: string[];
      cwd: () => string;
      exit: (code?: number | string | null) => never;
    };
  }
}

// Polyfill for Node.js path module
// Provides minimal path utilities for browser environment
if (typeof window !== 'undefined' && !window.path) {
  window.path = {
    resolve: (...paths: string[]) => paths.join('/').replace(/\/+/g, '/'),
    join: (...paths: string[]) => paths.join('/').replace(/\/+/g, '/'),
    dirname: (path: string) => {
      const lastSlash = path.lastIndexOf('/');
      return lastSlash === -1 ? '.' : path.substring(0, lastSlash);
    },
    basename: (path: string) => {
      const lastSlash = path.lastIndexOf('/');
      return lastSlash === -1 ? path : path.substring(lastSlash + 1);
    },
    extname: (path: string) => {
      const lastDot = path.lastIndexOf('.');
      return lastDot === -1 ? '' : path.substring(lastDot);
    }
  };
}

// Add path to window interface
declare global {
  interface Window {
    path?: {
      resolve: (...paths: string[]) => string;
      join: (...paths: string[]) => string;
      dirname: (path: string) => string;
      basename: (path: string) => string;
      extname: (path: string) => string;
    };
  }
}

// Layout forcing prevention mechanism
// This patch helps prevent "Layout was forced" warnings by deferring forced reflow operations
if (typeof window !== 'undefined') {
  // Track if layout forcing prevention is already active
  let layoutForcingPreventionActive = false;
  
  // Enhanced layout forcing prevention with CSS approach
  // This is safer than patching native methods and more reliable
  const preventLayoutForcing = () => {
    // Only apply once during initial page load
    if (document.readyState !== 'complete' && 
        document.readyState !== 'interactive' && 
        !layoutForcingPreventionActive) {
      
      layoutForcingPreventionActive = true;
      console.warn('[MJ] Layout Forcing Prevention Active');
      
      // Add the prevention class
      document.documentElement.classList.add('prevent-forced-layout');
      
      // Create a function to safely remove prevention
      const removeForceLayoutPrevention = () => {
        // Use triple-nested requestAnimationFrame to avoid layout forcing
        // This gives the browser time to apply all styles before removing the prevention
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              document.documentElement.classList.remove('prevent-forced-layout');
              layoutForcingPreventionActive = false;
              console.log('[MJ] Layout Forcing Prevention Removed');
            });
          });
        });
      };
      
      // Remove prevention when the page is fully loaded
      document.addEventListener('DOMContentLoaded', () => {
        // Wait a bit after DOMContentLoaded to ensure all styles are applied
        setTimeout(removeForceLayoutPrevention, 100);
      }, { once: true });
      
      // Absolute fallback timeout in case DOMContentLoaded doesn't fire
      setTimeout(removeForceLayoutPrevention, 2000);
    }
  };
  
  // Call immediately to ensure early protection
  preventLayoutForcing();
}

// Export a dummy value to make this a module
export const polyfillsLoaded = true;